<template>
  <v-container class="py-10">
    <!-- ฉลากเครื่องมือแพทย์ (อ้างอิง SD-ITD-206 (IFU) Rev.00) -->
    <v-card class="label-card mb-10 rounded-lg" elevation="2">
      <div class="label-header px-6 py-5">
        <div class="d-flex align-center flex-wrap">
          <v-icon large color="white" class="mr-3">mdi-medical-bag</v-icon>
          <div>
            <div class="text-overline white--text label-overline">ฉลากเครื่องมือแพทย์ · Medical Device Label</div>
            <div class="text-h5 font-weight-bold white--text">{{ product.name }}</div>
            <div class="text-subtitle-1 white--text label-subtitle">{{ product.nameTh }}</div>
          </div>
          <v-spacer></v-spacer>
          <div class="mt-3 mt-md-0">
            <v-chip small color="white" text-color="primary" class="font-weight-bold mr-2">Model {{ product.model }}</v-chip>
            <v-chip small outlined color="white">{{ product.docNo }}</v-chip>
          </div>
        </div>
      </div>

      <v-card-text class="pa-6">
        <v-row>
          <!-- (1) ชื่อผลิตภัณฑ์ -->
          <v-col cols="12" md="6">
            <div class="label-section">
              <div class="section-title"><span class="section-no">๑</span>ชื่อผลิตภัณฑ์</div>
              <div class="text-body-1 font-weight-medium">{{ product.name }}</div>
              <div class="grey--text text--darken-1">({{ product.nameTh }})</div>
            </div>
          </v-col>

          <!-- (5) ผู้ผลิต -->
          <v-col cols="12" md="6">
            <div class="label-section">
              <div class="section-title"><span class="section-no">๕</span>ชื่อของผู้ผลิตหรือเจ้าของผลิตภัณฑ์</div>
              <div class="text-body-1 font-weight-medium">{{ manufacturer.nameTh }}</div>
              <div class="grey--text text--darken-1">{{ manufacturer.address }}</div>
            </div>
          </v-col>

          <!-- (2) วัตถุประสงค์การใช้ -->
          <v-col cols="12">
            <div class="label-section">
              <div class="section-title"><span class="section-no">๒</span>วัตถุประสงค์การใช้</div>
              <ul class="label-list">
                <li v-for="(p, i) in purposes" :key="i">{{ p }}</li>
              </ul>
              <div class="d-flex flex-wrap mt-3">
                <v-chip
                  v-for="(d, i) in riskDomains"
                  :key="i"
                  small
                  label
                  color="blue lighten-5"
                  text-color="blue darken-3"
                  class="mr-2 mb-2"
                >
                  <span class="font-weight-bold mr-1">{{ i + 1 }}.</span>{{ d }}
                </v-chip>
              </div>
            </div>
          </v-col>

          <!-- (3) ข้อบ่งใช้ -->
          <v-col cols="12">
            <div class="label-section">
              <div class="section-title"><span class="section-no">๓</span>ข้อบ่งใช้</div>
              <v-row dense>
                <v-col v-for="(item, i) in indications" :key="i" cols="12" sm="6" md="3">
                  <div class="info-tile pa-3 fill-height">
                    <v-icon color="primary" class="mb-1">{{ item.icon }}</v-icon>
                    <div class="font-weight-bold grey--text text--darken-3 mb-1">{{ item.title }}</div>
                    <div class="text-body-2 grey--text text--darken-1">{{ item.text }}</div>
                  </div>
                </v-col>
              </v-row>
            </div>
          </v-col>

          <!-- (4) รายละเอียดเครื่องมือแพทย์ -->
          <v-col cols="12">
            <div class="label-section">
              <div class="section-title"><span class="section-no">๔</span>รายละเอียดเกี่ยวกับเครื่องมือแพทย์</div>

              <div class="sub-title">ประเภท / ชนิด</div>
              <p class="mb-4">{{ device.type }}</p>

              <div class="sub-title">หลักการทำงาน</div>
              <p class="mb-4">{{ device.principle }}</p>

              <div class="sub-title">ส่วนประกอบสำคัญ</div>
              <v-row dense>
                <v-col v-for="(c, i) in device.components" :key="i" cols="12" md="6">
                  <div class="d-flex align-start component-item pa-3 fill-height">
                    <v-avatar size="28" color="primary" class="mr-3 white--text text-body-2 font-weight-bold flex-shrink-0">{{ i + 1 }}</v-avatar>
                    <div>
                      <div class="font-weight-bold grey--text text--darken-3">{{ c.title }}</div>
                      <div class="text-body-2 grey--text text--darken-1">{{ c.text }}</div>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </div>
          </v-col>

          <!-- (6) เวอร์ชัน -->
          <v-col cols="12">
            <div class="label-section">
              <div class="section-title"><span class="section-no">๖</span>เวอร์ชัน / รุ่นที่ผลิต</div>
              <v-row dense>
                <v-col v-for="(v, i) in versionInfo" :key="i" cols="12" sm="4">
                  <div class="version-tile pa-3 text-center">
                    <div class="text-caption grey--text text--darken-1">{{ v.label }}</div>
                    <div class="text-subtitle-1 font-weight-bold primary--text">{{ v.value }}</div>
                  </div>
                </v-col>
              </v-row>
            </div>
          </v-col>

          <!-- (7) คำเตือน ข้อห้ามใช้ ข้อควรระวัง -->
          <v-col cols="12">
            <div class="section-title"><span class="section-no warn">๗</span>คำเตือน ข้อห้ามใช้ หรือข้อควรระวัง</div>
            <v-alert
              border="left"
              colored-border
              type="warning"
              elevation="1"
              class="mb-3"
            >
              <div class="font-weight-bold mb-1">คำเตือน</div>
              {{ warning }}
            </v-alert>
            <v-alert border="left" colored-border type="error" elevation="1" class="mb-0">
              <div class="font-weight-bold mb-1">ข้อควรระวัง / ข้อห้ามใช้</div>
              <ul class="label-list mb-0">
                <li v-for="(c, i) in cautions" :key="i">{{ c }}</li>
              </ul>
            </v-alert>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-row justify="center" align="center">
      
      <v-col cols="12" md="7">
        <v-img
          :src="require('@/assets/map.png')"
          alt="Innotech Map"
          max-width="100%"
          contain
          class="rounded-lg elevation-1"
        ></v-img>
      </v-col>

      <v-col cols="12" md="5">
        <v-card flat class="transparent">
          <v-card-title class="text-h4 font-weight-bold primary--text px-0 mb-6">
            Location
          </v-card-title>

          <v-card-text class="px-0 text-body-1 black--text text--lighten-1">
            <div class="text-h5 font-weight-regular mb-4" style="color: #37474F;">
              Innotech Laboratory Service Co., Ltd.
            </div>

            <div class="mb-6 line-height-relaxed">
              <p class="mb-1">697 Srinakarin Road, Pattanakarn</p>
              <p class="mb-1">Suanluang, Bangkok 10250</p>
            </div>

            <v-list dense class="transparent pa-0">
              
              <v-list-item class="px-0 min-height-auto mb-2">
                <v-list-item-content class="pa-0">
                  <div>
                    <span class="font-weight-medium grey--text text--darken-2">Tel: </span>
                    <span>+(66) 2 320 5132-5</span>
                  </div>
                </v-list-item-content>
              </v-list-item>

              <v-list-item class="px-0 min-height-auto mb-2">
                <v-list-item-content class="pa-0">
                  <div>
                    <span class="font-weight-medium grey--text text--darken-2">E-mail: </span>
                    <a href="mailto:info@innotechlab.co.th" class="text-decoration-none primary--text">info@innotechlab.co.th</a>
                  </div>
                </v-list-item-content>
              </v-list-item>

              <v-list-item class="px-0 min-height-auto mb-2">
                <v-list-item-content class="pa-0">
                  <div>
                    <span class="font-weight-medium grey--text text--darken-2">Website: </span>
                    <a href="http://www.innotechlab.net" target="_blank" class="text-decoration-none primary--text">http://www.innotechlab.net</a>
                  </div>
                </v-list-item-content>
              </v-list-item>

              <v-list-item class="px-0 min-height-auto mb-2">
                <v-list-item-content class="pa-0">
                  <div>
                    <span class="font-weight-medium grey--text text--darken-2">Facebook: </span>
                    <a href="https://www.facebook.com/innotechlab" target="_blank" class="text-decoration-none primary--text">www.facebook.com/innotechlab</a>
                  </div>
                </v-list-item-content>
              </v-list-item>

              <v-list-item class="px-0 min-height-auto mb-2">
                <v-list-item-content class="pa-0">
                  <div>
                    <span class="font-weight-medium grey--text text--darken-2">Line: </span>
                    <a href="#" class="text-decoration-none primary--text">@innotechlab</a>
                  </div>
                </v-list-item-content>
              </v-list-item>

              <v-list-item class="px-0 min-height-auto mb-2">
                <v-list-item-content class="pa-0">
                  <div>
                    <span class="font-weight-medium grey--text text--darken-2">LinkedIn: </span>
                    <a href="#" target="_blank" class="text-decoration-none primary--text">Innotech Laboratory Service Co., Ltd.</a>
                  </div>
                </v-list-item-content>
              </v-list-item>

              <v-list-item class="px-0 min-height-auto mb-2">
                <v-list-item-content class="pa-0">
                  <div>
                    <span class="font-weight-medium grey--text text--darken-2">Google Maps: </span>
                    <a href="#" target="_blank" class="text-decoration-none primary--text">Innotech's Location</a>
                  </div>
                </v-list-item-content>
              </v-list-item>

            </v-list>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
export default {
  name: 'AboutPage',
  data () {
    return {
      // ข้อมูลฉลากอ้างอิงจากเอกสาร SD-ITD-206 (IFU) Rev.00
      product: {
        name: 'Intelligent Health OS',
        nameTh: 'ระบบปฏิบัติการสุขภาพอัจฉริยะ',
        model: '1.0.0',
        docNo: 'SD-ITD-206-(IFU) Rev.00'
      },
      manufacturer: {
        nameTh: 'บจก. อินโนเทค ลาบอราทอรี่ เซอร์วิส จำกัด',
        address: '697 ถ. ศรีนครินทร์ แขวงพัฒนาการ เขตสวนหลวง กรุงเทพมหานคร 10250'
      },
      purposes: [
        'เพื่อให้ผู้ใช้งานได้รับรายงานผลการวิเคราะห์สุขภาพเฉพาะบุคคลสำหรับประกอบการตัดสินใจ',
        'เพื่อให้ผู้ใช้งานได้รับรายงานผลการคัดกรองความเสี่ยงเบื้องต้นต่อการเกิดโรค และความผิดปกติ (Risk of Disease and Abnormality) เพื่อใช้ประกอบการตัดสินใจในการให้คำวินิจฉัยและวางแผนทางคลินิก ใน 13 กลุ่มโรค ดังนี้'
      ],
      riskDomains: [
        'เบาหวานชนิดที่ 2 (Type 2 Diabetes Mellitus Risk)',
        'ความดันโลหิตสูง (Hypertension Risk)',
        'โรคหัวใจและหลอดเลือด (Cardiovascular Disease Risk)',
        'มะเร็งตับ (Liver Cancer Risk)',
        'มะเร็งต่อมลูกหมาก (Prostate Cancer Risk / BPH Gray Zone)',
        'มะเร็งลำไส้ใหญ่และทวารหนัก (Colorectal Cancer Risk)',
        'มะเร็งรังไข่ (Ovarian Cancer Risk)',
        'มะเร็งตับอ่อนหรือท่อน้ำดี (Pancreaticobiliary Cancer Risk)',
        'โรคไตเรื้อรัง (Chronic Kidney Disease)',
        'ไขมันพอกตับ (MASLD/Fatty Liver Risk)',
        'ยูริกสูงและโรคเกาต์ (Hyperuricemia / Gout Risk)',
        'ไทรอยด์ทำงานผิดปกติ (Thyroid Dysfunction Risk)',
        'โลหิตจางและการขาดธาตุเหล็ก (Anemia & Iron Deficiency Risk)'
      ],
      indications: [
        { icon: 'mdi-account-tie', title: 'ผู้ใช้งาน', text: 'บุคลากรทางการแพทย์ที่ผ่านการอบรมการใช้งาน' },
        { icon: 'mdi-hospital-building', title: 'สถานที่ใช้งาน', text: 'สถานที่การใช้งานทั่วไป เช่น สถานพยาบาล โรงพยาบาล' },
        { icon: 'mdi-flask-outline', title: 'ข้อมูลรับเข้า', text: 'ผลการตรวจสุขภาพจากห้องปฏิบัติการ (Clinical lab results)' },
        { icon: 'mdi-web', title: 'ระบบที่รองรับ', text: 'Windows ผ่าน Google Chrome / macOS ผ่าน Safari เชื่อมต่ออินเทอร์เน็ตความเร็วขั้นต่ำ 1 Mbps' }
      ],
      device: {
        type: 'ระบบปัญญาประดิษฐ์พร้อมแอปพลิเคชันบนคอมพิวเตอร์ (Web application) สำหรับสร้างรายงานผลด้านสุขภาพเฉพาะบุคคล (Personal Health report)',
        principle: 'ระบบรับผลการตรวจทางห้องปฏิบัติการ ส่งข้อมูลในรูปแบบ FHIR ไปประมวลผลด้วยโมเดลปัญญาประดิษฐ์แบบ Rule-based engine ซึ่งอ้างอิงเกณฑ์ทางการแพทย์สากล (International medical guidelines) และผ่านการตรวจสอบจากแพทย์ผู้เชี่ยวชาญ แล้วให้ผลลัพธ์ 2 ส่วน ได้แก่ สรุปภาพรวมการคัดกรองความเสี่ยงสุขภาพ (Risk Screening Summary) และผลการวิเคราะห์เชิงลึกตามกลุ่มโรค (Risk Domain Analysis)',
        components: [
          { title: 'โมเดลปัญญาประดิษฐ์ (AI model)', text: 'Rule based logic system ประมวลผลข้อมูลรับเข้าเพื่อสร้างรายงานผลการวิเคราะห์สุขภาพ' },
          { title: 'ข้อมูลรับเข้า (Inputs)', text: 'ผลตรวจสุขภาพจากห้องปฏิบัติการ (Clinical lab results)' },
          { title: 'ข้อมูลส่งออก (Outputs)', text: 'รายงานผลที่ผ่านการวิเคราะห์ด้วยโมเดลปัญญาประดิษฐ์' },
          { title: 'Application Programming Interface (API)', text: 'ตัวกลางเชื่อมต่อระหว่างข้อมูลรับเข้าและข้อมูลส่งออก' },
          { title: 'เว็บแอปพลิเคชัน (Web application)', text: 'ส่วนต่อประสานผู้ใช้ (User interface) และแสดงผลข้อมูลส่งออก' }
        ]
      },
      versionInfo: [
        { label: 'รุ่น (Model)', value: '1.0' },
        { label: 'เลขที่เอกสารกำกับ', value: 'SD-ITD-206-(IFU)' },
        { label: 'ฉบับแก้ไข (Revision)', value: 'Rev.00' }
      ],
      warning: 'ข้อมูลที่ปรากฏในรายงานนี้เป็นผลการประเมินเบื้องต้นจากระบบเท่านั้น ไม่สามารถใช้ยืนยันการวินิจฉัยทางการแพทย์ได้ ขอแนะนำให้ปรึกษาแพทย์หรือผู้เชี่ยวชาญทางการแพทย์เพื่อรับคำแนะนำที่ถูกต้อง',
      cautions: [
        'ข้อมูลที่แสดงเป็นการให้ข้อมูลเพื่อให้แพทย์ใช้ประกอบการพิจารณาการตัดสินใจเท่านั้น ไม่ใช่ผลการวินิจฉัย',
        'ห้ามเปลี่ยนแปลงการตั้งค่าโปรแกรมโดยไม่ได้รับอนุญาต หากพบข้อบกพร่องในการใช้งานหรือการแสดงผล ให้ติดต่อผู้ผลิตเท่านั้น',
        'ควรได้รับการบำรุงรักษาทุก 1 ปี โดยผู้เชี่ยวชาญ',
        'กรุณาอ่านคู่มือการใช้งาน (IFU) โดยละเอียดก่อนใช้งาน'
      ]
    }
  }
}
</script>

<style scoped>
/* ช่วยปรับระยะบรรทัดและเคลียร์ padding ส่วนเกินของ Vuetify เพื่อให้ชิดซ้ายตรงกันสวยงาม */
.min-height-auto {
  min-height: auto !important;
}
.line-height-relaxed p {
  line-height: 1.6;
}

/* ฉลากเครื่องมือแพทย์ */
.label-card {
  overflow: hidden;
  border-top: 4px solid #0d47a1;
}
.label-header {
  background: linear-gradient(135deg, #0d47a1 0%, #1976d2 60%, #26a69a 100%);
}
.label-overline {
  opacity: 0.85;
  letter-spacing: 0.08em !important;
}
.label-subtitle {
  opacity: 0.9;
}
.label-section {
  height: 100%;
  padding-bottom: 8px;
  border-bottom: 1px dashed #e0e0e0;
}
.section-title {
  display: flex;
  align-items: center;
  font-size: 1.05rem;
  font-weight: 700;
  color: #0d47a1;
  margin-bottom: 10px;
}
.section-no {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-right: 10px;
  border-radius: 50%;
  background: #e3f2fd;
  color: #0d47a1;
  font-size: 0.95rem;
  flex-shrink: 0;
}
.section-no.warn {
  background: #fff3e0;
  color: #e65100;
}
.sub-title {
  font-weight: 700;
  color: #455a64;
  margin-bottom: 4px;
}
.label-list {
  padding-left: 20px;
  line-height: 1.7;
}
.info-tile,
.component-item {
  background: #f5f9ff;
  border: 1px solid #e3f2fd;
  border-radius: 8px;
}
.version-tile {
  border: 1px solid #bbdefb;
  border-radius: 8px;
  background: #fafcff;
}
</style>